import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormLabelDirective,
  RowComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import {
  PartnerApiToken,
  PartnerApiTokenService
} from '../../../services/partner-api-token.service';

/**
 * Card partner credentials: issue, inspect, revoke.
 *
 * Super admin only, matching the backend — a token issued here reads cardholder
 * names, addresses and dates of birth.
 *
 * The raw token is shown exactly once, right after creation: we store only its
 * SHA-256 digest, so a lost token cannot be recovered, only replaced.
 */
@Component({
  selector: 'app-partner-tokens-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    TableDirective,
    BadgeComponent,
    ButtonDirective,
    FormControlDirective,
    FormLabelDirective,
    TranslatePipe
  ],
  templateUrl: './partner-tokens-page.component.html',
  styleUrls: ['./partner-tokens-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PartnerTokensPageComponent {
  private logger = new Logger('PartnerTokensPageComponent');
  private service = inject(PartnerApiTokenService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  /** Must match PartnerApiToken::ALL_SCOPES on the backend. */
  readonly allScopes = ['users:read', 'fulfilment:write', 'card_events:write'];

  tokens = signal<PartnerApiToken[]>([]);
  loading = signal(false);
  creating = signal(false);
  error = signal<string | null>(null);

  /** The raw token, shown once. Cleared as soon as the admin dismisses it. */
  newToken = signal<string | null>(null);
  copied = signal(false);

  showForm = signal(false);
  formPartner = signal('card_partner');
  formDescription = signal('');
  formExpiresAt = signal('');
  formScopes = signal<string[]>([...this.allScopes]);

  constructor() {
    this.load();
  }

  private load(): void {
    this.loading.set(true);

    this.service.getTokens()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.tokens.set(response.tokens);
          this.loading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load partner API tokens:', err);
          this.error.set(err?.error?.error || this.translate.translate('partner_tokens.load_failed'));
          this.loading.set(false);
        }
      });
  }

  toggleForm(): void {
    this.showForm.update(open => !open);
    this.error.set(null);
  }

  toggleScope(scope: string): void {
    this.formScopes.update(current =>
      current.includes(scope) ? current.filter(s => s !== scope) : [...current, scope]
    );
  }

  hasScope(scope: string): boolean {
    return this.formScopes().includes(scope);
  }

  /** 'users:read' -> 'partner_tokens.scope_users_read' (a colon cannot be a key segment). */
  scopeLabelKey(scope: string): string {
    return `partner_tokens.scope_${scope.replace(':', '_')}`;
  }

  create(): void {
    if (this.formScopes().length === 0) {
      this.error.set(this.translate.translate('partner_tokens.scopes_required'));
      return;
    }

    this.creating.set(true);
    this.error.set(null);

    this.service.createToken({
      partner: this.formPartner() || undefined,
      description: this.formDescription() || undefined,
      scopes: this.formScopes(),
      expires_at: this.formExpiresAt() || undefined
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.creating.set(false);
          this.newToken.set(response.token);
          this.copied.set(false);
          this.showForm.set(false);
          this.formDescription.set('');
          this.formExpiresAt.set('');
          this.load();
        },
        error: err => {
          this.creating.set(false);
          this.error.set(err?.error?.error || this.translate.translate('partner_tokens.create_failed'));
          this.logger.error('Failed to create partner API token:', err);
        }
      });
  }

  copyToken(): void {
    const token = this.newToken();
    if (!token) return;

    navigator.clipboard?.writeText(token).then(
      () => this.copied.set(true),
      () => this.copied.set(false)
    );
  }

  dismissToken(): void {
    this.newToken.set(null);
    this.copied.set(false);
  }

  revoke(token: PartnerApiToken): void {
    // A revoked token stops working immediately for the partner — worth a confirm.
    const question = this.translate.translate('partner_tokens.revoke_confirm');
    if (!confirm(`${question}\n\n#${token.id} ${token.description || token.partner}`)) return;

    this.service.revokeToken(token.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.load(),
        error: err => {
          this.error.set(err?.error?.error || this.translate.translate('partner_tokens.revoke_failed'));
          this.logger.error('Failed to revoke partner API token:', err);
        }
      });
  }

  statusColor(token: PartnerApiToken): string {
    if (token.revoked_at) return 'danger';
    if (!token.active) return 'secondary';
    return 'success';
  }

  statusKey(token: PartnerApiToken): string {
    if (token.revoked_at) return 'partner_tokens.status_revoked';
    if (!token.active) return 'partner_tokens.status_expired';
    return 'partner_tokens.status_active';
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '–';
    return new Date(value).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
